/* ---------------------------------------------------------------------------
   Which rating a bridge stands on, what it posts, and what that makes the
   bridge: open, posted at N t, or closed. LIFECYCLE.md `LC-22` and `LC-23`.

   ONE FILE, BOTH SIDES, as src/data/phase.ts is. functions/lib/ratings.ts
   picks the governing rating with these functions when an approver accepts
   one, and writes its posting; Bridge Rating and the register draw the same
   answer from the same functions. Two copies would drift, and the drift that
   matters is the register showing a posting the server did not write.

   THE GOVERNING RATING IS THE LOWEST, NOT THE NEWEST. A bridge is as strong as
   its weakest span and girder: a later interior-girder rating of 1.4 does not
   make an exterior girder that rated 0.8 any stronger. Each span and girder
   position has at most one current rating — the accepted one, which the next
   accepted rating of the same span and position supersedes — and the bridge
   takes the lowest factor across them.
   --------------------------------------------------------------------------- */

import type { RatingResult } from '../cbplr/index.ts';
import { phaseOf, type PhaseFields } from './phase.ts';

/** The fields of a rating this file reads — both the stored row and the client's record carry them. */
export interface RatingLike {
  id: string;
  bridgeId: string;
  state: 'draft' | 'review' | 'accepted' | 'superseded';
  spanIndex: number | null;
  position: string | null;
  governingRf: number | null;
  postingRequired: boolean;
  ratedAt: string;
  acceptedAt: string | null;
}

/** When a rating became the decision of record: its acceptance, else when it was made. */
export function acceptedOn(r: Pick<RatingLike, 'acceptedAt' | 'ratedAt'>): string {
  return r.acceptedAt ?? r.ratedAt;
}

/** The span and girder a rating is of, as one key. A single-span rating says no span. */
export function memberKey(r: Pick<RatingLike, 'spanIndex' | 'position'>): string {
  return `${r.spanIndex ?? 0}|${r.position ?? ''}`;
}

/**
 * The ratings a bridge currently stands on: accepted, one per span and girder
 * position. The server keeps it so (migration 0037's unique index); this still
 * takes the newest acceptance where a list holds two, so a stale page cannot
 * show both.
 */
export function currentRatings<T extends RatingLike>(ratings: readonly T[], bridgeId: string): T[] {
  const byMember = new Map<string, T>();
  for (const r of ratings) {
    if (r.bridgeId !== bridgeId || r.state !== 'accepted') continue;
    const key = memberKey(r);
    const held = byMember.get(key);
    if (held === undefined || acceptedOn(r) > acceptedOn(held)) byMember.set(key, r);
  }
  return [...byMember.values()];
}

/**
 * The governing rating: the lowest rating factor across the current ratings.
 * A rating with no factor (nothing in the denominator) governs only where no
 * other does. Ties go to the newer acceptance.
 */
export function governingRating<T extends RatingLike>(ratings: readonly T[], bridgeId: string): T | null {
  let best: T | null = null;
  for (const r of currentRatings(ratings, bridgeId)) {
    if (best === null) {
      best = r;
      continue;
    }
    const a = r.governingRf ?? Infinity;
    const b = best.governingRf ?? Infinity;
    if (a < b || (a === b && acceptedOn(r) > acceptedOn(best))) best = r;
  }
  return best;
}

/**
 * The tonnes a rating posts the bridge at: the lowest restricted vehicle's
 * limit, rounded DOWN to 0.1 t, since a sign that rounds up posts more than the
 * bridge carries, and never below zero — a section that cannot carry its own
 * weight posts nothing across it. Null where the rating requires no posting.
 */
export function postingTonnes(result: Pick<RatingResult, 'posting' | 'postingRequired'> | null): number | null {
  if (result === null || !result.postingRequired) return null;
  const limits = result.posting.filter((p) => !p.unrestricted).map((p) => p.tonnes);
  if (limits.length === 0) return null;
  return Math.max(0, Math.floor(Math.min(...limits) * 10 + 1e-9) / 10);
}

/* --------------------------- the posting on the bridge --------------------------- */

/**
 * Where `loadRestriction` came from (LC-23), as `Bridge.posting` carries it.
 *
 * `rating`: written by accepting the governing rating, which `ratingId` names.
 * `manual`: set through the inventory form. A bridge with a restriction and no
 * `posting` has one on file whose origin nobody recorded.
 */
export interface Posting {
  source: 'rating' | 'manual';
  ratingId: string | null;
  by: string | null;
  at: string | null;
}

export type OperationalKind = 'open' | 'posted' | 'closed' | 'not-open';

/** What a road user needs from the register: may they cross, and at what weight. */
export interface OperationalStatus {
  kind: OperationalKind;
  /** "Open", "Posted 12.9 t", "Closed", "Not open". */
  label: string;
  /** Who decided, and when. Null where nobody recorded it. */
  by: string | null;
  at: string | null;
  /** What decided it: a rating, a hand edit, a phase decision, or a value on file. */
  source: 'rating' | 'manual' | 'decision' | 'on-file' | null;
  /** The rating that set the posting, where one did. */
  ratingId: string | null;
  /** What moved the bridge to where it stands, where `source` is `decision`. */
  cause?: 'create' | 'baseline' | 'handover' | 'rating' | 'decision' | null;
}

export interface OperationalFields extends PhaseFields {
  loadRestriction: number | null;
  phaseAt?: string | null;
  phaseBy?: string | null;
  phaseOn?: string | null;
  phaseCause?: 'create' | 'baseline' | 'handover' | 'rating' | 'decision' | null;
  posting?: Posting | null;
}

/**
 * Open, posted at N t, or closed — and on whose decision.
 *
 * Closed and not-yet-open come from the phase (LC-10). A load restriction on an
 * in-service bridge is a posting, whatever set it; the restricted flag with no
 * tonnes on file is a restriction somebody decided without a figure.
 */
export function operationalStatus(b: OperationalFields): OperationalStatus {
  const p = phaseOf(b);
  const decided = {
    by: p.recorded ? (b.phaseBy ?? null) : null,
    at: p.recorded ? (b.phaseAt ?? null) : null,
    cause: p.recorded ? (b.phaseCause ?? null) : null,
  };
  if (p.phase === 'closed') return { kind: 'closed', label: 'Closed', ...decided, source: 'decision', ratingId: null };
  if (p.phase === 'planned' || p.phase === 'construction') {
    return { kind: 'not-open', label: 'Not open', ...decided, source: 'decision', ratingId: null };
  }
  if (b.loadRestriction != null) {
    const posting = b.posting ?? null;
    return {
      kind: 'posted',
      label: `Posted ${b.loadRestriction} t`,
      by: posting?.by ?? null,
      at: posting?.at ?? null,
      source: posting?.source ?? 'on-file',
      ratingId: posting?.ratingId ?? null,
    };
  }
  if (p.restricted) return { kind: 'posted', label: 'Restricted', ...decided, source: 'decision', ratingId: null };
  return { kind: 'open', label: 'Open', ...decided, source: p.recorded ? 'decision' : null, ratingId: null };
}

/** What moved a bridge to where it stands, as the Decided line names it. */
export const CAUSE_LABEL: Record<'create' | 'baseline' | 'handover' | 'rating' | 'decision', string> = {
  create: 'record created',
  baseline: 'baseline accepted',
  handover: 'handover accepted',
  rating: 'rating accepted',
  decision: 'status decision',
};

/**
 * Since when the bridge has stood where it does, and by whose hand: the day the
 * transition took effect, and the day it was recorded where those differ —
 * "12 Aug 26 · recorded 30 Sep 26 · a.approver".
 */
export function sinceLine(b: Pick<OperationalFields, 'phaseAt' | 'phaseBy' | 'phaseOn'>, pretty: (iso: string) => string): string {
  const recorded = b.phaseAt ? b.phaseAt.slice(0, 10) : null;
  if (recorded === null) return '—';
  const on = b.phaseOn ?? recorded;
  return [pretty(on), on !== recorded ? `recorded ${pretty(recorded)}` : null, b.phaseBy ?? null].filter(Boolean).join(' · ');
}

/** Who decided and when, as one line — "rating 3f2a… · a.approver · 2024-11-06". */
export function decidedLine(s: OperationalStatus): string {
  const what =
    s.source === 'rating'
      ? `rating ${s.ratingId ?? ''}`.trim()
      : s.source === 'manual'
        ? 'set by hand'
        : s.source === 'on-file'
          ? 'on file, source not recorded'
          : s.source === 'decision'
            ? CAUSE_LABEL[s.cause ?? 'decision']
            : 'not recorded';
  return [what, s.by, s.at ? s.at.slice(0, 10) : null].filter(Boolean).join(' · ');
}
